import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import DataGrid from '../../../components/DataGrid';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {useModal} from '../../../contexts/ModalContext';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import LyricsModal from './LyricsModal';

const Lyrics = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();

    const [lyrics, setLyrics] = useState([]);
    const [categories, setCategories] = useState([]);

    // filtry
    const [selectedAlbumIds, setSelectedAlbumIds] = useState([]); // Array<string>
    const [query, setQuery] = useState(''); // hledá v názvu i textu

    const fetchLyrics = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.lyrics.list);
            setLyrics(res.data || []);
        } catch (e) {
            console.error('Failed to fetch lyrics:', e);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.lyricsCategories.list);
            setCategories(res.data || []);
        } catch (e) {
            console.error('Failed to fetch lyrics categories:', e);
        }
    }, []);

    useEffect(() => {
        fetchLyrics();
    }, [fetchLyrics]);
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const openLyricsModal = (lyric) => {
        openModal(
            LyricsModal,
            {lyric, onLyricsUpdate: fetchLyrics, categories},
            {title: lyric ? t('lyrics.edit') : t('lyrics.add')}
        );
    };

    const confirmDelete = async (lyric) => {
        const ok = await confirm(t('lyrics.confirmDeletion', {name: lyric.name}));
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.lyrics.delete(lyric._id));
                setLyrics(prev => prev.filter(l => l._id !== lyric._id));
            } catch (e) {
                console.error('Failed to delete lyric:', e);
            }
        }
    };

    const normalized = useMemo(() => {
        // pro každý řádek dopočti categoryId a categoryName
        return (lyrics || []).map(l => {
            const cat = typeof l.category === 'object' && l.category !== null ? l.category : null;
            return {
                ...l,
                _categoryId: cat?._id || l.category || null,
                _categoryName: cat?.name || ''
            };
        });
    }, [lyrics]);

    const queryLc = query.trim().toLowerCase();
    const rows = useMemo(() => {
        return normalized.filter(l => {
            // filtr albumů (pokud je něco vybráno)
            if (selectedAlbumIds.length > 0) {
                if (!l._categoryId || !selectedAlbumIds.includes(String(l._categoryId))) {
                    return false;
                }
            }
            // hledání v názvu + textu
            if (queryLc) {
                const hay = `${l.name || ''} ${l.text || ''}`.toLowerCase();
                if (!hay.includes(queryLc)) return false;
            }
            return true;
        });
    }, [normalized, selectedAlbumIds, queryLc]);

    const toggleAlbum = (id) => {
        setSelectedAlbumIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const clearFilters = () => {
        setSelectedAlbumIds([]);
        setQuery('');
    };

    const columns = [
        {
            key: 'name',
            name: t('lyrics.name'),
            width: '28%',
            renderCell: ({row}) => (
                <label className="flex justify-between">
                    {row.name}
                    <div className="flex">
                        <DeleteIcon className="w-4 cursor-pointer fill-red-700 m-2" onClick={() => confirmDelete(row)}/>
                        <EditIcon className="w-4 cursor-pointer text-blue-800 h-full"
                                  onClick={() => openLyricsModal(row)}/>
                    </div>
                </label>
            )
        },
        {
            key: '_categoryName',
            name: t('lyrics.album'),
            width: '18%',
            renderCell: ({row}) => row._categoryName || '-'
        },
        {
            key: 'text',
            name: t('lyrics.text'),
            width: '34%',
            renderCell: ({row}) => {
                const s = (row.text || '').replace(/\s+/g, ' ').trim();
                return s.length > 150 ? `${s.slice(0, 150)}…` : s || '-';
            }
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            width: '20%',
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    return (
        <div>
            <div className="flex items-start justify-between mb-4">
                <Forms.Button label={t('lyrics.add')} onClick={() => openLyricsModal()}/>

                <div className="flex flex-col gap-3 items-end">
                    {/* query: název i text */}
                    <Forms.Text
                        label={t('search')}
                        value={query}
                        onChange={setQuery}
                    />

                    {/* alba: checkboxy */}
                    <div className="w-full">
                        <div className="font-medium mb-1">{t('lyrics.albums')}</div>
                        <div className="flex flex-wrap gap-3">
                            {(categories || []).map(cat => {
                                const id = String(cat._id);
                                const checked = selectedAlbumIds.includes(id);
                                return (
                                    <label key={id} className="inline-flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer"
                                            checked={checked}
                                            onChange={() => toggleAlbum(id)}
                                        />
                                        <span>{cat.name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {(selectedAlbumIds.length > 0 || query) && (
                        <div className="self-end">
                            <Forms.Button variant="secondary" onClick={clearFilters} label={t('clearFilters')}/>
                        </div>
                    )}
                </div>
            </div>

            <DataGrid rows={rows} columns={columns}/>
        </div>
    );
};

export default Lyrics;
